// Runsheet Service for API interactions
// Based on the API endpoints from the test file and VB.NET code functionality

import { apiEndpoints } from '../config/env'
import { apiService } from './apiService'

// Types based on API structure
export interface RunsheetClient {
  clientID: number
  clientName: string
}

export interface RunsheetWorkOrder {
  workOrderID: number
  clientID: number
  propertyID: number
  propertyAddress: string
  buildingName: string
  keyPickup?: boolean
  puKeyNo?: string
  puComment?: string
  puAvailable?: boolean
  doKeyNo?: string
  doComment?: string
  doAvailable?: boolean
  puSignature?: string
  doSignature?: string
  puDate?: string
  doDate?: string
  clientNotes?: string
}

export interface Technician {
  id: number
  name: string
  email: string
  username: string
}

export interface KeyUpdateRequest {
  workOrderID: number
  propertyID: number
  puKeyNo?: string
  puComment?: string
  puAvailable?: boolean
  doKeyNo?: string
  doComment?: string
  doAvailable?: boolean
}

export interface SignatureRequest {
  workOrderIDs: number[]
  clientID: number
  keyType: 'PU' | 'DO'
  signatureBase64: string
}

export interface SignatureResponse {
  signatureUrl?: string
  message?: string
}

// Local storage keys for signatures
const SIGNATURE_STORAGE_KEY = 'coastsmoke_technician_signature'

class RunsheetService {
  // Get technicians list
  async getTechnicians(): Promise<Technician[]> {
    try {
      const data = await apiService.get<Technician[]>(
        `${apiEndpoints.runsheet}/technicians`
      )
      return data || []
    } catch (error) {
      console.error('Error fetching technicians:', error)
      throw error
    }
  }

  // Get runsheet data for technician and date
  async getRunsheet(technicianId: number, date: string): Promise<RunsheetClient[]> {
    try {
      const data = await apiService.get<RunsheetClient[]>(
        `${apiEndpoints.runsheet}?technicianId=${technicianId}&date=${date}&ClientTypeID=2`
      )
      return data || []
    } catch (error) {
      console.error('Error fetching runsheet:', error)
      throw error
    }
  }

  // Get client work orders for a specific date and technician
  async getClientWorkOrders(clientId: number, date: string, technicianId: number): Promise<RunsheetWorkOrder[]> {
    try {
      const data = await apiService.get<RunsheetWorkOrder[]>(
        `${apiEndpoints.runsheet}/client/${clientId}/workorders?date=${date}&technicianId=${technicianId}`
      )
      return data || []
    } catch (error) {
      console.error('Error fetching client work orders:', error)
      throw error
    }
  }

  // Get today's runsheet
  async getTodaysRunsheet(): Promise<RunsheetClient[]> {
    try {
      const data = await apiService.get<RunsheetClient[]>(`${apiEndpoints.runsheet}/today`)
      return data || []
    } catch (error) {
      console.error('Error fetching today\'s runsheet:', error)
      throw error
    }
  }

  // Update key information
  async updateKeyInfo(keyUpdate: KeyUpdateRequest): Promise<void> {
    try {
      await apiService.put<void>(`${apiEndpoints.runsheet}/key`, keyUpdate)
    } catch (error) {
      console.error('Error updating key info:', error)
      throw error
    }
  }

  // Save signature
  async saveSignature(signatureData: SignatureRequest): Promise<SignatureResponse> {
    try {
      const data = await apiService.post<SignatureResponse>(
        `${apiEndpoints.runsheet}/signature`,
        signatureData
      )
      return data
    } catch (error) {
      console.error('Error saving signature:', error)
      throw error
    }
  }

  // Get signature
  async getSignature(workOrderIds: string, keyType: 'PU' | 'DO', clientId: number): Promise<SignatureResponse> {
    try {
      const data = await apiService.get<SignatureResponse>(
        `${apiEndpoints.runsheet}/signature?workOrderIds=${workOrderIds}&keyType=${keyType}&clientId=${clientId}`
      )
      return data
    } catch (error) {
      console.error('Error getting signature:', error)
      throw error
    }
  }

  // Local storage methods for signature persistence
  saveSignatureToStorage(signatureBase64: string): void {
    try {
      localStorage.setItem(SIGNATURE_STORAGE_KEY, signatureBase64)
    } catch (error) {
      console.error('Error saving signature to storage:', error)
    }
  }

  getSignatureFromStorage(): string | null {
    try {
      return localStorage.getItem(SIGNATURE_STORAGE_KEY)
    } catch (error) {
      console.error('Error retrieving signature from storage:', error)
      return null
    }
  }

  clearSignatureFromStorage(): void {
    try {
      localStorage.removeItem(SIGNATURE_STORAGE_KEY)
    } catch (error) {
      console.error('Error clearing signature from storage:', error)
    }
  }
}

export const runsheetService = new RunsheetService() 